<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import AnimateHeight from '@/components/AnimateHeight.vue';
import KSearch from '@/components/KSearch.vue';
import ProxySelect from '@/components/ProxySelect.vue';
import { enabled } from '@/feature-toggle';
import { useAccessRightsStore } from '@/stores/accessRights/accessRights';
import { useAppInfoStore } from '@/stores/appInfo';
import IconBarChart from '~icons/lucide/chart-no-axes-column-increasing';
import IconProfile from '~icons/lucide/cloud-cog';
import IconSettings from '~icons/lucide/cog';
import IconDistribution from '~icons/lucide/expand';
import IconFileJson from '~icons/lucide/file-json-2';
import IconHistory from '~icons/lucide/history';
import IconLicense from '~icons/lucide/id-card';
import IconLayout from '~icons/lucide/layout';
import IconLifebuoy from '~icons/lucide/life-buoy';
import IconLockOpen from '~icons/lucide/lock-open';
import IconMerge from '~icons/lucide/merge';
import IconVM from '~icons/lucide/monitor-cog';
import IconBlueprint from '~icons/lucide/pencil-ruler';
import IconScroll from '~icons/lucide/scroll';
import IconMulticluster from '~icons/lucide/server';
import IconApps from '~icons/lucide/ship-wheel';
import IconUsers from '~icons/lucide/users-round';

import KSidebarLink from './KSidebarLink.vue';
import KSidebarList from './KSidebarList.vue';

const appInfoStore = useAppInfoStore();
const accessRightsStore = useAccessRightsStore();
const route = useRoute();

const props = withDefaults(
  defineProps<{
    mobile?: boolean;
  }>(),
  { mobile: false },
);

const newPolicyPresetsPageEnabled = computed(() => enabled('legacyPresets') === false);

const newDistributionsPageEnabled =
  // TODO: Remove this, when the new distributions page will be by default
  !enabled('legacyDistributions');

const singleClusterNavItems = computed(() => {
  return [
    { display: 'Dashboard', routeName: 'Dashboard', icon: IconLayout },
    { display: 'Applications', routeName: 'Apps', icon: IconApps },
    { display: 'Restore Points', routeName: 'RestorePointsCatalog', icon: IconHistory },
    {
      display: 'Virtual Machines',
      routeName: 'VMs',
      icon: IconVM,
    },
    {
      display: 'Policies',
      icon: IconScroll,

      children: [
        {
          display: 'Policies',
          routeName: 'Policies',
        },
        {
          display: newPolicyPresetsPageEnabled.value ? 'Presets (New)' : 'Presets',
          routeName: 'PolicyPresets',
        },
      ],
    },
    {
      display: 'Profiles',
      icon: IconProfile,

      children: [
        { display: 'Location', routeName: 'LocationProfiles' },
        { display: 'Infrastructure', routeName: 'InfraProfiles' },
      ],
    },
    {
      display: 'Blueprints',
      icon: IconBlueprint,
      children: [
        { display: 'Blueprints', routeName: 'BlueprintsList' },
        { display: 'Bindings', routeName: 'BlueprintBindings' },
      ],
    },
    {
      display: 'Transform Sets',
      icon: IconFileJson,
      children: [
        { display: 'Transform Sets', routeName: 'TransformSetsList' },
        { display: 'Examples', routeName: 'TransformSetsExamples' },
      ],
    },
    {
      display: 'Usage & Reports',
      icon: IconBarChart,
      children: [
        { display: 'Data Usage', routeName: 'Data' },
        { display: 'Reports', routeName: 'Reports' },
      ],
    },
    {
      display: 'User Roles',
      icon: IconUsers,
      children: [
        { display: 'Roles', routeName: 'UserRoles' },
        { display: 'Assignments', routeName: 'UserRoleAssignments' },
      ],
    },
    {
      display: 'Settings',
      icon: IconSettings,

      children: [
        { display: 'System Information', routeName: 'SystemInfo' },
        { display: 'Licenses', routeName: 'LicenseSettings' },
        { display: 'Registrations', routeName: 'Registrations' },

        ...(accessRightsStore.isAdmin()
          ? [
              { display: 'Passkey Management', routeName: 'PasskeyManagement' },
              { display: 'Setup Kasten DR', routeName: 'DRSetup' },
              { display: 'Restore Kasten', routeName: 'DRRestore' },
            ]
          : []),
      ],
    },
    ...(!appInfoStore.useMCLayout && appInfoStore.isMCCapable && accessRightsStore.isAdmin()
      ? [{ display: 'Multi-Cluster', routeName: 'MCSecondaryPage', icon: IconMulticluster }]
      : []),
  ];
});

const multiClusterNavItems = computed(() => {
  return [
    {
      display: 'Clusters',
      routeName: 'MCClusters',
      icon: IconMulticluster,
    },
    {
      display: 'Global Policies',
      routeName: 'MCPolicies',
      icon: IconScroll,
    },
    {
      display: 'Global Profiles',
      icon: IconProfile,

      children: [
        { display: 'Location', routeName: 'MCLocationProfiles' },
        { display: 'Infrastructure', routeName: 'MCInfraProfiles' },
      ],
    },
    { display: 'User Permissions', routeName: 'MCRBAC', icon: IconLockOpen },
    {
      display: newDistributionsPageEnabled ? 'Distributions' : 'Distributions (legacy)',
      routeName: 'MCDistributions',
      icon: IconDistribution,
    },

    // add the Licensing menu item if the feature is enabled
    ...(appInfoStore.systemInfo?.features.includes('mc-primary')
      ? [{ display: 'Licensing', routeName: 'MCLicensing', icon: IconLicense }]
      : []),
    // add the MC menu item if the feature is enabled
    ...(accessRightsStore.allowed('list', 'secretsMC')
      ? [{ display: 'Join Tokens', routeName: 'MCJoinTokens', icon: IconMerge }]
      : []),
  ];
});

const supportItem = { display: 'Get Support', routeName: 'Support' };

const isMobile = props.mobile === true;

const useMCLayout = computed(() => appInfoStore.useMCLayout);
const isMCPage = computed(() => !!route.meta?.isMC);
</script>

<template>
  <div
    class="flex grow flex-col overflow-y-auto border-r border-gray-100 bg-white dark:border-navy-600 dark:bg-navy-950"
  >
    <!-- SEARCH (mobile only) -->
    <div
      v-if="isMobile && !isMCPage"
      class="border-b border-gray-100 px-4 py-3 dark:border-navy-600"
    >
      <KSearch full-width />
    </div>

    <nav
      class="flex flex-1 flex-col overflow-hidden"
      data-testid="sidebar-nav"
      aria-label="Primary"
    >
      <AnimateHeight>
        <div v-if="useMCLayout" class="mb-3 flex items-center px-6 pt-5 font-bold">
          <ProxySelect />
        </div>
      </AnimateHeight>
      <div
        :class="[
          'scrollbox flex flex-1 flex-col gap-y-7 overflow-y-auto px-6 pb-5',
          !useMCLayout && 'pt-5',
        ]"
      >
        <transition name="fade" mode="out-in">
          <!-- MULTI-CLUSTER -->
          <div v-if="isMCPage">
            <KSidebarList :items="multiClusterNavItems" :use-proxy="false" :mobile="isMobile" />
          </div>
          <!-- SINGLE CLUSTER -->
          <div v-else>
            <KSidebarList :items="singleClusterNavItems" :mobile="isMobile" />
          </div>
        </transition>
      </div>
      <div
        class="mt-auto flex h-[53px] flex-shrink-0 items-center border-t border-black/10 bg-white px-6 dark:border-navy-600 dark:bg-navy-950"
      >
        <KSidebarLink
          :item="supportItem"
          :current="route.name === supportItem.routeName"
          class="-mx-2 w-full"
        >
          <IconLifebuoy class="h-6 w-6 shrink-0 text-gray-600" aria-hidden="true" />
          Get Support
        </KSidebarLink>
      </div>
    </nav>
  </div>
</template>
